import {
  changeDirectionShort,
  changeDirectionWord,
  compareToReference,
  formatComparatorPhrase,
  formatPercentMagnitude,
  formatSignedPercentChange,
  isFiniteNumber,
  sectorSharePercent,
} from './narrativeFormatting';
import type { NarrativeFacts, NarrativeViewState } from './types';
import {
  buildElectricityClimatePlansCalculation,
  buildElectricityPrimarySources,
  ELECTRICITY_TECHNICAL_PROVENANCE,
} from './electricityUpstreamMethodology';
import { renderAuthoredNarrativeBundle, getAuthoredOverview } from './authoredNarrativeRender';
import { buildElectricityNarrativeTokens } from './electricityNarrativeTokens';
import { buildElectricityChartProvenanceGuidance } from './energyEstimatedYearsCopy';

export type ElectricityViewId = 'total' | 'res' | 'com' | 'perCapita';

export interface ElectricitySectorMetricsInput {
  latestValueKwh: number;
  latestYear: number;
  baselineYear: number | null;
  valueBaselineKwh: number | null;
  changePercentSinceBaseline: number | null;
}

export interface ElectricityPerCapitaPair {
  year: number;
  localKwhPerPerson: number;
  californiaKwhPerPerson: number | null;
}

export interface ElectricityConsumptionNarrativeInput {
  locationName: string;
  viewId: ElectricityViewId;
  total?: ElectricitySectorMetricsInput;
  residential?: ElectricitySectorMetricsInput;
  commercial?: ElectricitySectorMetricsInput;
  /** Latest year with both res + com for share KPIs */
  compositionYear?: number;
  residentialKwh?: number | null;
  commercialKwh?: number | null;
  perCapita?: ElectricityPerCapitaPair | null;
  perCapitaBaseline?: { year: number; local: number } | null;
  formatGwh: (kwh: number) => string;
  commercialLabel?: string;
  residentialExtrapModelLabel?: string | null;
  commercialExtrapModelLabel?: string | null;
  usesEia861Pilot?: boolean;
  utilityName?: string;
  dataPeriod?: string;
  hasEstimatedYears?: boolean;
}

export function buildElectricityNarrativeFacts(input: ElectricityConsumptionNarrativeInput): NarrativeFacts {
  const { viewId, locationName } = input;
  if (viewId === 'perCapita' && input.perCapita) {
    const { year, localKwhPerPerson, californiaKwhPerPerson } = input.perCapita;
    const cmp = compareToReference(localKwhPerPerson, californiaKwhPerPerson);
    let pctChange: number | null = null;
    if (input.perCapitaBaseline && input.perCapitaBaseline.local > 0) {
      pctChange =
        ((localKwhPerPerson - input.perCapitaBaseline.local) / input.perCapitaBaseline.local) * 100;
    }
    return {
      metricId: 'electricity_per_capita',
      metricLabel: 'Electricity per capita',
      currentValue: localKwhPerPerson,
      currentYear: year,
      unit: 'kWh/person',
      baselineValue: input.perCapitaBaseline?.local ?? null,
      baselineYear: input.perCapitaBaseline?.year ?? null,
      percentChange: pctChange,
      comparatorValue: californiaKwhPerPerson,
      comparatorYear: year,
      comparatorLabel: 'California',
      comparatorDifference: cmp.difference,
      comparatorPercentDifference: cmp.percentDifference,
      provenanceStatus: input.hasEstimatedYears ? 'mixed' : 'observed',
    };
  }

  const sector =
    viewId === 'res'
      ? input.residential
      : viewId === 'com'
        ? input.commercial
        : input.total;
  const metricId =
    viewId === 'total'
      ? 'electricity_res_plus_com'
      : viewId === 'res'
        ? 'electricity_residential'
        : 'electricity_commercial';
  const metricLabel =
    viewId === 'total'
      ? 'Residential + reported commercial electricity'
      : viewId === 'res'
        ? 'Residential electricity'
        : input.commercialLabel ?? 'Reported commercial electricity';

  const resShare =
    viewId !== 'perCapita' && isFiniteNumber(input.residentialKwh) && isFiniteNumber(input.commercialKwh)
      ? sectorSharePercent(input.residentialKwh!, input.residentialKwh! + input.commercialKwh!)
      : null;
  const comShare =
    resShare != null ? Math.round((100 - resShare) * 10) / 10 : null;

  return {
    metricId,
    metricLabel,
    currentValue: sector?.latestValueKwh ?? null,
    currentYear: sector?.latestYear ?? null,
    unit: 'kWh',
    baselineValue: sector?.valueBaselineKwh ?? null,
    baselineYear: sector?.baselineYear ?? null,
    percentChange: sector?.changePercentSinceBaseline ?? null,
    componentShares:
      viewId === 'total' && resShare != null && comShare != null
        ? { residential: resShare, commercial: comShare }
        : viewId === 'res' && resShare != null
          ? { residentialShareOfPair: resShare }
          : viewId === 'com' && comShare != null
            ? { commercialShareOfPair: comShare }
            : undefined,
    provenanceStatus: input.hasEstimatedYears ? 'mixed' : 'observed',
    limitations: input.usesEia861Pilot
      ? ['EIA-861 municipal pilot; CAP comparison bars are not the same as EIA nonresidential.']
      : undefined,
  };
}

const CHART_ID = 'electricity_consumption';

export function electricityOverviewDescription(viewId: ElectricityViewId, commercialLabel: string): string {
  const fallback =
    viewId === 'com'
      ? `This view shows ${commercialLabel.toLowerCase()} customer-class electricity only.`
      : 'Electricity consumption for the selected customer-class view.';
  return getAuthoredOverview(CHART_ID, viewId, fallback);
}

export function buildElectricityAuthoredBundle(
  input: ElectricityConsumptionNarrativeInput,
  ctx?: { dataSourcesFromDb?: string[]; methodsFromDb?: string[] }
) {
  const tokens = buildElectricityNarrativeTokens(input);
  const fallbackMethodology = buildElectricityMethodologySteps(input, ctx);
  const bundle = renderAuthoredNarrativeBundle(CHART_ID, input.viewId, tokens, {
    keyInsights: '',
    methodology: fallbackMethodology,
  });
  enrichElectricityMethodologyWithProvenance(bundle, input);
  return bundle;
}

function enrichElectricityMethodologyWithProvenance(
  bundle: ReturnType<typeof renderAuthoredNarrativeBundle>,
  input: ElectricityConsumptionNarrativeInput
): void {
  if (!bundle.methodology) return;
  const hasEstimated = Boolean(input.hasEstimatedYears);
  const provenance = buildElectricityChartProvenanceGuidance(hasEstimated, input.locationName);
  const alreadyHasProvenance = bundle.methodology.calculation.some(
    (line) => /chart legend/i.test(line) || /^For .+, each year/.test(line)
  );
  if (!alreadyHasProvenance) {
    bundle.methodology.calculation = [...provenance, ...bundle.methodology.calculation];
  }
  bundle.methodology.limitations = bundle.methodology.limitations.filter(
    (line) =>
      !/chart colors distinguish/i.test(line) &&
      !/estimated years must remain/i.test(line) &&
      !/visually and narratively distinguishable/i.test(line)
  );
  if (hasEstimated) {
    bundle.methodology.limitations.push(
      'Do not treat Interpolated or Extrapolated bars as utility-reported totals; read the chart legend and the active extrapolation model above the chart.'
    );
  } else {
    bundle.methodology.limitations.push(
      `For ${input.locationName}, all displayed electricity years in the active charts are Observed (utility-reported); gap-fill models are not applied to the series shown.`
    );
  }
}

export function buildElectricityKeyInsights(input: ElectricityConsumptionNarrativeInput): string {
  const bundle = buildElectricityAuthoredBundle(input);
  if (bundle.keyInsights) return bundle.keyInsights;
  const loc = input.locationName;
  if (input.viewId === 'perCapita' && !input.perCapita) {
    return `Per-capita electricity data are not available for a common reporting year in ${loc}.`;
  }
  const sector =
    input.viewId === 'res' ? input.residential : input.viewId === 'com' ? input.commercial : input.total;
  if (input.viewId !== 'perCapita' && (!sector || sector.latestValueKwh <= 0)) {
    return `Electricity data are not available for the selected view in ${loc}.`;
  }
  return '';
}

export function buildElectricityMethodologySteps(
  input: ElectricityConsumptionNarrativeInput,
  ctx?: {
    dataSourcesFromDb?: string[];
    methodsFromDb?: string[];
  }
): {
  primarySources: string[];
  calculation: string[];
  limitations: string[];
  technicalDetails: string[];
} {
  const primarySources = buildElectricityPrimarySources({
    locationName: input.locationName,
    dataSourcesFromDb: ctx?.dataSourcesFromDb ?? (input.utilityName ? [input.utilityName] : []),
    methodsFromDb: ctx?.methodsFromDb ?? [],
    usesEia861Pilot: input.usesEia861Pilot,
    residentialExtrapModelLabel: input.residentialExtrapModelLabel,
    commercialExtrapModelLabel: input.commercialExtrapModelLabel,
    viewId: input.viewId,
  });

  const calculation = [
    ...buildElectricityChartProvenanceGuidance(Boolean(input.hasEstimatedYears), input.locationName),
    ...buildElectricityClimatePlansCalculation({
      locationName: input.locationName,
      dataSourcesFromDb: ctx?.dataSourcesFromDb ?? [],
      methodsFromDb: ctx?.methodsFromDb ?? [],
      usesEia861Pilot: input.usesEia861Pilot,
      residentialExtrapModelLabel: input.residentialExtrapModelLabel,
      commercialExtrapModelLabel: input.commercialExtrapModelLabel,
      viewId: input.viewId,
    }),
  ];

  const limitations = [
    'Reported commercial does not necessarily include all nonresidential classes (e.g., industrial may be excluded from stacked totals).',
    'Utility totals can include EV charging billed to residential or commercial accounts.',
  ];
  if (input.hasEstimatedYears) {
    limitations.push(
      'Do not treat Interpolated or Extrapolated bars as utility-reported totals; read the chart legend and the active extrapolation model above the chart.'
    );
  } else {
    limitations.push(
      `For ${input.locationName}, all displayed electricity years in the active charts are Observed (utility-reported).`
    );
  }

  return {
    primarySources,
    calculation,
    limitations,
    technicalDetails: ELECTRICITY_TECHNICAL_PROVENANCE,
  };
}

export function narrativeViewStateForElectricity(
  route: string,
  viewId: ElectricityViewId,
  locationName: string,
  methodIds?: { residential?: string; commercial?: string }
): NarrativeViewState {
  return {
    route,
    sectionId: 'electricity_consumption',
    chartId: 'electricity_consumption',
    viewId,
    locationName,
    methodId: viewId === 'res' ? methodIds?.residential : viewId === 'com' ? methodIds?.commercial : undefined,
  };
}

export { formatSignedPercentChange };
